import React, { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { catPathFor } from "../lib/categories";
import { carDetailPath } from "../lib/seo";
import { colorForCar, displayVehicleName } from "../lib/carDisplay";
import QuickReserve from "./QuickReserve";
import VehicleCardImage from "./VehicleCardImage";

export default function CategoryVehicleRow({ category, l10n, cars }) {
  const { t, lang, price } = useApp();
  const rowRef = useRef(null);
  const [position, setPosition] = useState(1);
  const total = cars.length;
  const swipeHint = lang === "TR" ? "Devamı için kaydırın →" : lang === "RU"
    ? "Листайте дальше →"
    : "Swipe for more →";

  const updatePosition = () => {
    const element = rowRef.current;
    if (!element) {
      return;
    }

    const maxScroll = element.scrollWidth - element.clientWidth;
    const progress = maxScroll > 0 ? element.scrollLeft / maxScroll : 0;
    setPosition(
      Math.min(total, Math.max(1, Math.round(progress * (total - 1)) + 1)),
    );
  };

  const scrollRow = (direction) => {
    const element = rowRef.current;
    if (!element) {
      return;
    }

    element.scrollBy({ left: direction * 340, behavior: "smooth" });
    window.setTimeout(updatePosition, 360);
  };
  const cardCopy = lang === "TR"
    ? { power: "Güç", seats: "Koltuk", color: "Renk", from: "Başlangıç" }
    : lang === "RU"
      ? { power: "Мощность", seats: "Места", color: "Цвет", from: "От" }
      : { power: "Power", seats: "Seats", color: "Colour", from: "From" };

  return (
    <div data-testid={`category-row-${category.key}`}>
      <div className="mb-9 flex items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-3 font-mono-jb text-[11px] font-semibold uppercase tracking-[0.28em] text-teal-800">
            <span className="h-px w-10 bg-teal-700" />
            {t.fleet.title}
          </p>
          <h3 className="mt-4 font-cinzel text-2xl font-bold uppercase leading-none text-slate-950 sm:text-3xl">
            {l10n.name}
          </h3>
          <p className="mt-4 text-base text-slate-600">{l10n.sub}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={() => scrollRow(-1)}
            data-testid={`category-scroll-left-${category.key}`}
            aria-label={`${l10n.name} previous vehicles`}
            className="flex h-12 w-12 items-center justify-center border border-slate-500 bg-transparent
              text-slate-700 transition-[background-color,border-color,color,transform] duration-200
              hover:-translate-x-0.5 hover:border-teal-700 hover:bg-teal-50 hover:text-teal-800"
          >
            <ChevronLeft size={19} />
          </button>
          <button
            type="button"
            onClick={() => scrollRow(1)}
            data-testid={`category-scroll-right-${category.key}`}
            aria-label={`${l10n.name} next vehicles`}
            className="flex h-12 w-12 items-center justify-center border border-slate-500 bg-transparent
              text-slate-700 transition-[background-color,border-color,color,transform] duration-200
              hover:translate-x-0.5 hover:border-teal-700 hover:bg-teal-50 hover:text-teal-800"
          >
            <ChevronRight size={19} />
          </button>
          <Link
            to={catPathFor(category)}
            data-testid={`category-viewall-${category.key}`}
            className="hidden border-b-2 border-teal-700 pb-1 text-sm font-semibold uppercase tracking-wide text-slate-900 transition-colors duration-200 hover:text-teal-800 sm:inline"
          >
            {t.fleet.viewAll || "View all →"}
          </Link>
        </div>
      </div>
      <div className="relative">
        <div
          ref={rowRef}
          onScroll={updatePosition}
          data-testid={`category-vehicle-scroll-${category.key}`}
          className="category-vehicle-scroll -mx-5 flex snap-x snap-mandatory gap-6 overflow-x-auto px-5 pb-5 sm:-mx-1 sm:px-1"
        >
          {cars.map((car) => {
            const status = car.availability || car.status || "available";
            const vehicleTitle = displayVehicleName(car);

            return (
              <article
                key={car.id}
                data-testid={`category-vehicle-card-${car.id}`}
                className="w-[88vw] shrink-0 snap-start border border-slate-300 bg-white sm:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-3rem)/3)]"
              >
                <Link
                  to={carDetailPath(car)}
                  data-testid={`category-vehicle-image-${car.id}`}
                  className="relative block aspect-[16/10] overflow-hidden bg-slate-100"
                >
                  <VehicleCardImage
                    sources={[car.card_image, car.main_image]}
                    alt={`${car.name} ${t.seo.altSuffix}`}
                    data-testid={`category-vehicle-card-image-${car.id}`}
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
                    placeholderClassName="h-full w-full bg-slate-100"
                  />
                  <span className="absolute left-0 top-0 inline-flex items-center gap-2 bg-white px-3 py-2 font-mono-jb text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700">
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        status === "available" ? "bg-emerald-600" : "bg-amber-500"
                      }`}
                    />
                    {t.fleet[status] || t.fleet.available}
                  </span>
                </Link>
                <div className="p-5">
                  <p className="font-mono-jb text-[10px] font-semibold uppercase tracking-[0.28em] text-teal-800">
                    {car.brand} · {car.model_year || "—"}
                  </p>
                  <h4 className="mt-3 min-h-7 font-cinzel text-xl font-bold uppercase leading-none text-slate-950">
                    {vehicleTitle}
                  </h4>
                  <div className="mt-5 grid grid-cols-3 border-l border-t border-slate-200">
                    {[
                      [cardCopy.power, car.specs?.horsepower || "—"],
                      [cardCopy.seats, car.specs?.seats || "—"],
                      [cardCopy.color, colorForCar(car)],
                    ].map(([label, value]) => (
                      <div key={label} className="border-b border-r border-slate-200 p-3">
                        <p className="font-mono-jb text-[9px] uppercase tracking-[0.16em] text-slate-500">
                          {label}
                        </p>
                        <p className="mt-2 text-sm font-bold text-slate-900">{value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex items-end justify-between gap-3">
                    <div>
                      <p className="font-mono-jb text-[9px] uppercase tracking-[0.18em] text-slate-500">
                        {cardCopy.from}
                      </p>
                      <p className="mt-1 font-cinzel text-3xl font-bold text-slate-950">
                        {price(car.daily_price_aed, car.daily_price_usd)}
                        <span className="ml-1 font-jakarta text-sm font-medium text-slate-600">
                          {t.fleet.perDay}
                        </span>
                      </p>
                    </div>
                    <Link
                      to={carDetailPath(car)}
                      data-testid={`category-vehicle-book-${car.id}`}
                      className="shrink-0 bg-teal-800 px-4 py-3 text-xs font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-teal-900"
                    >
                      {t.fleet.viewDetails}
                    </Link>
                  </div>
                  <QuickReserve car={car} testIdSuffix={category.key} />
                </div>
              </article>
            );
          })}
        </div>
        <div className="pointer-events-none absolute bottom-5 right-0 top-0 hidden w-20 bg-gradient-to-l from-[#F4F1EB] to-transparent sm:block" />
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <span
          data-testid={`category-swipe-hint-${category.key}`}
          className="text-xs font-medium text-teal-700"
        >
          {total > 1 ? swipeHint : ""}
        </span>
        <span
          data-testid={`category-vehicle-counter-${category.key}`}
          className="font-mono-jb text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-700"
        >
          {position} / {total}
        </span>
      </div>
    </div>
  );
}